import { useState, useEffect, useMemo, useRef, useCallback, Fragment } from 'react'
import { supabase } from '../lib/supabase'
import * as XLSX from 'xlsx'
import { resolveCurrentForecastState } from '../lib/buildCostControlData'

const fmt = v => v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

function monthKey(d) { return d.toISOString().slice(0, 7) } // 'YYYY-MM'
function monthLabel(ym) {
  const [y, m] = ym.split('-')
  return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)-1] + " '" + y.slice(2)
}
function firstOfMonth(ym) { return ym + '-01' }

async function fetchAllRows(queryFn) {
  const PAGE = 1000
  let from = 0
  let all = []
  while (true) {
    const { data } = await queryFn(from, from + PAGE - 1)
    if (!data?.length) break
    all = all.concat(data)
    if (data.length < PAGE) break
    from += PAGE
  }
  return all
}

// ── SSOT boundary (per approved architecture) ──────────────────────────────
// Cost Control Current Forecast = SINGLE SOURCE OF TRUTH for forecast values,
// AND for the hierarchy itself. This file never invents a parallel tree —
// it reuses the exact same resolved trees CostControl.jsx renders:
//   DIRECT:   Cost Code (parent) → Resource Code (parent.resourceRows[]) →
//             Sub Code (resourceRows[].children[])
//   INDIRECT: Cost Code Category (parent) → Sub Code (parent.children[])
//             — Indirect has no Resource Code level; resourceRows[] is
//             empty for every Indirect parent (verified against
//             buildCumulativeData()'s own construction — Indirect Sub Codes
//             attach straight to the parent Cost Code, never to a Resource).
// Every node in these trees already carries `isOwner`/`resolvedEac` from
// resolveDraft() (the same resolution resolveCurrentForecastState() wraps).
// This file only ever READS these fields — it never recomputes or
// overrides a forecast value, and only Distributable nodes (isOwner nodes,
// or a childless/non-forecasted Resource) get an editable monthly row; every
// other level is a pure aggregation/display row, matching Cost Control's
// own parent-total behavior exactly.
// ── SSOT boundary (per approved architecture) ──────────────────────────────
// Cost Control Current Forecast = SINGLE SOURCE OF TRUTH. This file performs
// ZERO forecast resolution, fallback, ownership, or aggregation logic of its
// own. It calls the EXACT SAME function CostControl.jsx calls, with the same
// arguments, and renders the EXACT tree that function returns — unmodified.
//   DIRECT:   Cost Code (parent) → Resource Code (parent.resourceRows[]) →
//             Sub Code (resourceRows[].children[])
//   INDIRECT: Cost Code (parent, flat array — no Category level exists in
//             this tree; Cost Control itself only groups by Category as a
//             UI tab filter, never as part of the Forecast resolution tree
//             — see resolveDraft()/buildForecastStructure(), neither of
//             which produces or reads any Category grouping) → Sub Code
//             (parent.children[]) — Indirect has no Resource Code level.
// Every node already carries `isOwner`, `hasEffectiveForecast`, and
// `resolvedEac` from resolveDraft() — this file only READS those fields to
// decide which row gets a monthly input; it never recomputes them.

async function loadForecastTrees(projectId) {
  const [resolvedDirect, resolvedIndirect] = await Promise.all([
    resolveCurrentForecastState(projectId, 'direct'),
    resolveCurrentForecastState(projectId, 'indirect'),
  ])
  return { direct: resolvedDirect, indirect: resolvedIndirect }
}

// Pure display utility — NOT forecast logic. A node is the distributable
// (editable) row for Cash Out purposes exactly when it is a leaf in the
// EXISTING Cost Control tree (no resourceRows, no children). This is a
// structural read of the tree Cost Control already returned — it does not
// decide ownership, does not compute EAC, and does not alter resolvedEac.
function isLeafNode(node) {
  return (node.resourceRows || []).length === 0 && (node.children || []).length === 0
}

// The single Distribution Owner rule, reused identically in the three
// places that must agree on it: showing the Distribute button
// (renderNode), building the list saveAll() persists, and any Check
// calculation tied to distribution. This mirrors the exact Forecast
// ownership already established for VAT/Remaining — a node is the owner
// (and therefore the only node distribution is written under) when either
// it has an explicit Decision (isOwner===true) or its resolvedEac is not
// itself a sum of effective descendants (hasEffectiveForecast!==true).
// `parentIsDistributionOwner` prevents a descendant from claiming
// ownership again once an ancestor has already claimed it — this is what
// keeps distribution from becoming duplicated across levels (e.g. both a
// Cost Code AND its Resource Codes independently distributable for the
// same underlying Forecast).
function isDistributionOwner(node, parentIsDistributionOwner) {
  return !parentIsDistributionOwner && (node.isOwner === true || node.hasEffectiveForecast !== true)
}

export default function CashOutForecast({ projectId }) {
  const [settings, setSettings]       = useState(null)
  const [showSettings, setShowSettings] = useState(false)
  const [settingsForm, setSettingsForm] = useState({ project_start_month: '', project_end_month: '' })
  // project_vat_config is now the single source of truth for the
  // project's VAT rate -- cashflow_settings.project_vat_rate no longer
  // drives this calculation (see resolvedVatRate below). The column
  // itself is left untouched in the database for this phase; only this
  // page's read is redirected.
  const [projectVatConfig, setProjectVatConfig] = useState(null)
  useEffect(() => {
    if (!projectId) return
    supabase.rpc('get_project_vat_config', { p_project_id: projectId }).then(({ data }) => {
      setProjectVatConfig(data?.[0] || null)
    })
  }, [projectId])

  const [directTree, setDirectTree]     = useState([])
  const [indirectTree, setIndirectTree] = useState([])
  const [paidMap, setPaidMap]           = useState({})
  const [unmatchedPaid, setUnmatchedPaid] = useState({ total: 0, rows: [] })
  const [expanded, setExpanded]         = useState({})   // { [nodeKey]: true } — absence = collapsed (see isExpanded below)

  // VAT treatment per item — reuses the SAME item_id/item_type identity
  // convention already used by cashflow_monthly_distribution. Does NOT
  // touch Current Forecast (Cost Control) in any way — this is purely a
  // Cash Out–side display/planning adjustment, applied on top of the
  // unmodified resolvedEac read elsewhere in this file.
  const [vatTreatment, setVatTreatment] = useState({})  // { [item_id]: 'vat_applicable' | 'no_vat' }
  // Per-item distribution metadata: { [item_id]: { distribution_method, last_distribution_period_start, last_distribution_period_end } }
  const [itemDistMeta, setItemDistMeta] = useState({})
  // { [item_id]: true } — items flagged as needing Period Change review.
  // Detection-only, computed after load; never written to the database by
  // itself. Cleared/recomputed whenever dist, itemDistMeta, or the current
  // project period changes.
  const [periodChangeFlags, setPeriodChangeFlags] = useState({})
  // Tracks which items had a distribution Apply this session, and what
  // item-level metadata to persist once the main Save succeeds — never
  // written before that. { [item_id]: { method } } for Manual Distribute,
  // or { [item_id]: { periodOnly: true } } for Period Change recalculation
  // (which must never overwrite distribution_method).
  const [pendingDistMeta, setPendingDistMeta] = useState({})

  // Distribution modal — proposes/previews an allocation of a leaf item's
  // positive `remaining` across chosen future months, writing ONLY into
  // the existing `dist` state / cashflow_monthly_distribution table on
  // confirm. Never touches Current Forecast, Forecast with VAT, or Paid.
  const [distModal, setDistModal] = useState(null)
  // { itemId, level ('sub_code'|'resource_code'|'cost_code'), isIndirect,
  //   remaining, method, selectedMonths: Set<ym>, monthlyAverage }

  const [dist, setDist]               = useState({})
  const [dirty, setDirty]             = useState({})
  const [saving, setSaving]           = useState(false)

  const [months, setMonths]           = useState([])
  const [loading, setLoading]         = useState(true)

  // Floating horizontal scroll bar for the wide monthly table — same
  // mechanism as IPC.jsx's ipcEqScrollRef/ipcEqScrollState pattern: a
  // fixed-position bar pinned to the bottom of the viewport (not the
  // table), tracking the scroll container's actual left/right screen
  // position so it lines up with the table regardless of page scroll.
  const scrollRef = useRef(null)
  const [scrollState, setScrollState] = useState({ hasOverflow: false, atStart: true, atEnd: true, ratio: 0 })
  const [scrollNavLeftRight, setScrollNavLeftRight] = useState({ left: 0, right: 0 })
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const hasOverflow = el.scrollWidth > el.clientWidth + 1
    const maxScroll = el.scrollWidth - el.clientWidth
    const ratio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0
    setScrollState({ hasOverflow, atStart: el.scrollLeft <= 1, atEnd: maxScroll <= 1 || el.scrollLeft >= maxScroll - 1, ratio })
    const rect = el.getBoundingClientRect()
    setScrollNavLeftRight({ left: rect.left, right: window.innerWidth - rect.right })
  }, [])
  const scrollBy = (dir) => { const el = scrollRef.current; if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.75), behavior: 'smooth' }) }
  const scrollToRatio = (ratio) => { const el = scrollRef.current; if (!el) return; const maxScroll = el.scrollWidth - el.clientWidth; el.scrollLeft = ratio * maxScroll }

  useEffect(() => {
    updateScrollState()
    const el = scrollRef.current
    if (!el) return
    const ro = new ResizeObserver(updateScrollState)
    ro.observe(el)
    const onResize = () => updateScrollState()
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', updateScrollState, true)
    return () => { ro.disconnect(); window.removeEventListener('resize', onResize); window.removeEventListener('scroll', updateScrollState, true) }
  }, [directTree, indirectTree, months, updateScrollState])


  useEffect(() => { if (projectId) loadAll(projectId) }, [projectId])

  async function loadAll(pid) {
    setLoading(true)
    await Promise.all([loadSettings(pid), loadForecast(pid), loadDist(pid), loadVatTreatment(pid)])
    setLoading(false)
  }

  // Detection-only — READ-ONLY, never writes to the database. Recomputes
  // whenever any of its inputs change (tree loaded, distribution loaded,
  // per-item metadata loaded, or the project period itself changes).
  // Flags exactly the items meeting all three locked eligibility criteria:
  //   1. is a CURRENT isDistributionOwner (per flattenDistributable()),
  //   2. has an actual saved distribution (a real, non-zero-only entry in
  //      dist[itemId] — i.e. it was genuinely distributed before), and
  //   3. has a non-null last_distribution_period_start/end that differs
  //      from the current project period.
  // A newly added/never-distributed item never satisfies (2) or (3) (its
  // itemDistMeta has null period fields), so it is correctly never flagged
  // — this is what distinguishes an actual period change from ordinary
  // "not yet allocated" newly visible months.
  useEffect(() => {
    if (!settings?.project_start_month || !settings?.project_end_month) { setPeriodChangeFlags({}); return }
    const currentStart = settings.project_start_month.slice(0, 10)
    const currentEnd = settings.project_end_month.slice(0, 10)
    const owners = [...flattenDistributable(directTree), ...flattenDistributable(indirectTree)]
    const flags = {}
    for (const item of owners) {
      const itemId = item.code
      const itemDist = dist[itemId] || {}
      const hasActualDistribution = Object.values(itemDist).some(v => (parseFloat(v) || 0) !== 0)
      if (!hasActualDistribution) continue  // criterion 2
      const meta = itemDistMeta[itemId]
      if (!meta || !meta.last_distribution_period_start || !meta.last_distribution_period_end) continue  // criterion 3 (null = never processed by either trigger — not a period-change case)
      const lastStart = meta.last_distribution_period_start.slice(0, 10)
      const lastEnd = meta.last_distribution_period_end.slice(0, 10)
      if (lastStart !== currentStart || lastEnd !== currentEnd) flags[itemId] = true
    }
    setPeriodChangeFlags(flags)
  }, [directTree, indirectTree, dist, itemDistMeta, settings?.project_start_month, settings?.project_end_month])

  // Fetched on demand (only when the distribution modal opens and the user
  // is on a code with enough history) rather than for every item on page
  // load — same Payment Entries source (cf_payment_entries) as paidMap
  // above, just grouped by month for this one code instead of summed.
  async function getHistoricalMonthlyPaid(itemIds, pid) {
    const codes = Array.isArray(itemIds) ? itemIds : [itemIds]
    if (codes.length === 0) return {}
    const { data } = await supabase.from('cf_payment_entries')
      .select('date, amount_inc_vat, sub_code').eq('project_id', pid).in('sub_code', codes)
    const byMonth = {}
    for (const r of (data || [])) {
      if (!r.date) continue
      const ym = r.date.slice(0, 7)
      byMonth[ym] = (byMonth[ym] || 0) + (parseFloat(r.amount_inc_vat) || 0)
    }
    return byMonth
  }

  async function loadForecast(pid) {
    const { direct, indirect } = await loadForecastTrees(pid)
    const paidRows = await fetchAllRows((from, to) =>
      supabase.from('cf_payment_entries').select('sub_code, amount_inc_vat').eq('project_id', pid).range(from, to))

    // Build the set of every actual leaf code that exists in the Cost
    // Control tree Cost Control itself returned (Direct Sub Codes under
    // Resource Codes, Direct orphan Sub Codes, Indirect Sub Codes) — used
    // only to separate matched vs. unmatched Payment Entries below. This
    // is a pure identity lookup, not a Forecast/Paid calculation.
    const knownCodes = new Set()
    function collectCodes(tree) {
      for (const parent of tree) {
        knownCodes.add((parent.code || '').trim().toLowerCase())
        for (const res of (parent.resourceRows || [])) {
          knownCodes.add((res.code || '').trim().toLowerCase())
          for (const sub of (res.children || [])) knownCodes.add((sub.code || '').trim().toLowerCase())
        }
        for (const sub of (parent.children || [])) knownCodes.add((sub.code || '').trim().toLowerCase())
      }
    }
    collectCodes(direct)
    collectCodes(indirect)

    const paid = {}
    let unmatchedTotal = 0
    const unmatchedRows = []
    for (const r of paidRows) {
      const key = (r.sub_code || '').trim().toLowerCase()
      if (!key) continue
      if (!knownCodes.has(key)) {
        // Sub Code on this Payment Entry does not exist anywhere in the
        // current Cost Control tree — per the reconciliation requirement,
        // this must be surfaced explicitly, never silently dropped or
        // folded into an existing node's total.
        unmatchedTotal += parseFloat(r.amount_inc_vat) || 0
        unmatchedRows.push(r)
        continue
      }
      paid[key] = (paid[key] || 0) + (parseFloat(r.amount_inc_vat) || 0)
    }

    setPaidMap(paid)
    setUnmatchedPaid({ total: unmatchedTotal, rows: unmatchedRows })
    setDirectTree(direct)
    setIndirectTree(indirect)
  }

  async function loadSettings(pid) {
    const { data } = await supabase.from('cashflow_settings').select('*').eq('project_id', pid).maybeSingle()
    setSettings(data)
    if (data?.project_start_month && data?.project_end_month) {
      setMonths(buildMonths(data.project_start_month, data.project_end_month))
      setSettingsForm({
        project_start_month: data.project_start_month.slice(0,7),
        project_end_month: data.project_end_month.slice(0,7),
      })
    }
  }

  async function loadVatTreatment(pid) {
    const { data } = await supabase.from('cashflow_item_vat_treatment')
      .select('item_id, vat_treatment, distribution_method, last_distribution_period_start, last_distribution_period_end')
      .eq('project_id', pid)
    const map = {}
    const metaMap = {}
    for (const r of (data || [])) {
      map[r.item_id] = r.vat_treatment
      metaMap[r.item_id] = {
        distribution_method: r.distribution_method,
        last_distribution_period_start: r.last_distribution_period_start,
        last_distribution_period_end: r.last_distribution_period_end,
      }
    }
    setVatTreatment(map)
    setItemDistMeta(metaMap)
  }

  async function setItemVatTreatment(itemId, treatment) {
    setVatTreatment(v => ({ ...v, [itemId]: treatment }))  // optimistic, matches this page's existing dist/dirty pattern
    const { data: existing } = await supabase.from('cashflow_item_vat_treatment')
      .select('id').eq('project_id', projectId).eq('item_type', ITEM_TYPE).eq('item_id', itemId).maybeSingle()
    if (existing) {
      await supabase.from('cashflow_item_vat_treatment').update({ vat_treatment: treatment }).eq('id', existing.id)
    } else {
      await supabase.from('cashflow_item_vat_treatment').insert({ project_id: projectId, item_type: ITEM_TYPE, item_id: itemId, vat_treatment: treatment })
    }
  }

  // Persists distribution_method and/or last_distribution_period_* for one
  // item. Called ONLY after a successful main Save (never on load, Review,
  // Preview, or Apply alone) — per the locked rule that these fields only
  // ever reflect a completed, saved distribution.
  //   - Manual Distribute save: updates BOTH distribution_method (the
  //     user's explicit choice) AND last_distribution_period_* (this item
  //     is now known-current for this exact period).
  //   - Period Change recalculation save: updates ONLY
  //     last_distribution_period_* — distribution_method is read-only here
  //     and must never be overwritten by a recalculation the user did not
  //     explicitly choose a new method for.
  async function persistItemDistMeta(itemId, { distributionMethod, periodStart, periodEnd }) {
    const payload = {}
    if (distributionMethod !== undefined) payload.distribution_method = distributionMethod
    if (periodStart !== undefined) payload.last_distribution_period_start = periodStart
    if (periodEnd !== undefined) payload.last_distribution_period_end = periodEnd
    if (Object.keys(payload).length === 0) return
    const { data: existing } = await supabase.from('cashflow_item_vat_treatment')
      .select('id').eq('project_id', projectId).eq('item_type', ITEM_TYPE).eq('item_id', itemId).maybeSingle()
    if (existing) {
      await supabase.from('cashflow_item_vat_treatment').update(payload).eq('id', existing.id)
    } else {
      await supabase.from('cashflow_item_vat_treatment').insert({ project_id: projectId, item_type: ITEM_TYPE, item_id: itemId, ...payload })
    }
    setItemDistMeta(m => ({ ...m, [itemId]: { ...(m[itemId]||{}), ...payload, distribution_method: payload.distribution_method ?? m[itemId]?.distribution_method, last_distribution_period_start: payload.last_distribution_period_start ?? m[itemId]?.last_distribution_period_start, last_distribution_period_end: payload.last_distribution_period_end ?? m[itemId]?.last_distribution_period_end } }))
  }

  function buildMonths(start, end) {
    const ms = []
    const s = new Date(start); s.setDate(1)
    const e = new Date(end);   e.setDate(1)
    while (s <= e) { ms.push(monthKey(s)); s.setMonth(s.getMonth()+1) }
    return ms
  }

  async function saveSettings() {
    // project_vat_rate intentionally omitted -- no longer written from
    // here; project_vat_config is the single Project VAT source now.
    // The column is left as-is in the database (not dropped) so any
    // existing stored value remains available for compatibility review.
    const payload = {
      project_id: projectId,
      project_start_month: firstOfMonth(settingsForm.project_start_month),
      project_end_month:   firstOfMonth(settingsForm.project_end_month),
    }
    const { data: existing } = await supabase.from('cashflow_settings').select('id').eq('project_id', projectId).maybeSingle()
    if (existing) await supabase.from('cashflow_settings').update(payload).eq('project_id', projectId)
    else await supabase.from('cashflow_settings').insert(payload)
    setShowSettings(false)
    loadSettings(projectId)
  }

  // item_type is a NEW value ('cc_forecast_item') distinct from the old
  // page's 'cc_package' — additive change: old cf_package_dictionary-based
  // rows (if any exist) are left completely untouched under their original
  // item_type, and never collide with these new Cost-Control-node-based rows.
  const ITEM_TYPE = 'cc_forecast_item'

  async function loadDist(pid) {
    const data = await fetchAllRows((from, to) =>
      supabase.from('cashflow_monthly_distribution').select('*')
        .eq('project_id', pid).eq('item_type', ITEM_TYPE).range(from, to))
    const map = {}
    for (const r of (data||[])) {
      if (!map[r.item_id]) map[r.item_id] = {}
      map[r.item_id][r.period_month.slice(0,7)] = parseFloat(r.amount)||0
    }
    setDist(map)
  }

  function handleCell(itemId, ym, val) {
    const num = parseFloat(val) || 0
    setDist(d => ({ ...d, [itemId]: { ...(d[itemId]||{}), [ym]: num } }))
    setDirty(d => ({ ...d, [`${itemId}|${ym}`]: true }))
  }

  // ── Remaining Distribution — pure calculation only, no side effects. ──
  // Every method below produces { [ym]: amount } for the selected months,
  // and every method reconciles SUM(values) === remaining exactly (the
  // rounding remainder is always absorbed into the LAST selected month,
  // consistent across all methods so behavior is predictable). None of
  // these functions read or write Current Forecast, Forecast with VAT, or
  // Paid — they only ever produce a proposal object that, on confirm, is
  // written into the SAME `dist`/`cashflow_monthly_distribution` state/
  // table used by the existing per-cell manual entry (handleCell above).
  function round2(n) { return Math.round(n * 100) / 100 }

  function distributeLinear(remaining, selectedMonths) {
    const n = selectedMonths.length
    if (n === 0) return {}
    const per = round2(remaining / n)
    const result = {}
    let running = 0
    selectedMonths.forEach((ym, i) => {
      if (i === n - 1) { result[ym] = round2(remaining - running) }
      else { result[ym] = per; running += per }
    })
    return result
  }

  // Shared helper: given normalized (sum-to-1) weights aligned to
  // selectedMonths, produce amounts that reconcile exactly to `remaining`.
  function applyWeights(remaining, selectedMonths, weights) {
    const totalWeight = weights.reduce((s,w) => s+w, 0) || 1
    const result = {}
    let running = 0
    selectedMonths.forEach((ym, i) => {
      if (i === selectedMonths.length - 1) { result[ym] = round2(remaining - running) }
      else { const amt = round2(remaining * (weights[i] / totalWeight)); result[ym] = amt; running += amt }
    })
    return result
  }

  function distributeFrontLoaded(remaining, selectedMonths) {
    const n = selectedMonths.length
    const weights = selectedMonths.map((_, i) => n - i)  // decreasing: n, n-1, ..., 1
    return applyWeights(remaining, selectedMonths, weights)
  }

  function distributeBackLoaded(remaining, selectedMonths) {
    const n = selectedMonths.length
    const weights = selectedMonths.map((_, i) => i + 1)  // increasing: 1, 2, ..., n
    return applyWeights(remaining, selectedMonths, weights)
  }

  function distributeSCurve(remaining, selectedMonths) {
    const n = selectedMonths.length
    if (n === 1) return applyWeights(remaining, selectedMonths, [1])
    // Normalized logistic curve sampled at each month's midpoint, scaled to
    // [-6, 6] so the first/last months start/end near the curve's flat tails
    // and the middle months carry the steep section — the classic S-Curve
    // shape used for cost/cash curves.
    const weights = selectedMonths.map((_, i) => {
      const x = -6 + (12 * (i + 0.5)) / n
      const logistic = 1 / (1 + Math.exp(-x))
      return logistic
    })
    // Convert the cumulative logistic samples into period weights (the
    // difference between consecutive points is the "hump" shape S-Curves
    // are meant to represent, rather than a monotonically increasing one).
    const periodWeights = weights.map((w, i) => i === 0 ? w : w - weights[i-1])
    const positiveWeights = periodWeights.map(w => Math.max(w, 0.0001))  // guard against any negative from floating point
    return applyWeights(remaining, selectedMonths, positiveWeights)
  }

  // Historical Payment Pattern — uses this item's OWN past Paid amounts
  // (from cf_payment_entries, the same Paid SSOT used everywhere else in
  // this file) as the weight curve. Requires at least 3 distinct historical
  // months of Paid activity for this item's code; otherwise this method is
  // not offered (see distModal UI below).
  function distributeHistorical(remaining, selectedMonths, historicalMonthlyPaid) {
    const historyValues = Object.values(historicalMonthlyPaid)
    if (historyValues.length < 3) return null  // not enough history — caller must not offer this method
    // Average share per calendar month-of-year (Jan..Dec) from history,
    // applied cyclically to the selected future months — a simple seasonal
    // pattern rather than requiring an exact historical date alignment.
    const byMonthOfYear = {}
    for (const [ym, amt] of Object.entries(historicalMonthlyPaid)) {
      const moy = ym.slice(5, 7)
      byMonthOfYear[moy] = (byMonthOfYear[moy] || 0) + amt
    }
    const weights = selectedMonths.map(ym => byMonthOfYear[ym.slice(5,7)] || 0)
    const allZero = weights.every(w => w === 0)
    return applyWeights(remaining, selectedMonths, allZero ? selectedMonths.map(() => 1) : weights)
  }

  // Monthly Average (Indirect Sub Code only, per approved scope) — the
  // user supplies a fixed amount per month; the LAST selected month is
  // adjusted so the total still reconciles exactly to `remaining`. This is
  // a distribution INPUT only, never a Forecast value, never stored
  // anywhere except the same monthly distribution table as every other
  // method here.
  function distributeMonthlyAverage(remaining, selectedMonths, monthlyAverage) {
    const n = selectedMonths.length
    if (n === 0) return {}
    const avg = parseFloat(monthlyAverage) || 0
    const result = {}
    let running = 0
    selectedMonths.forEach((ym, i) => {
      if (i === n - 1) { result[ym] = round2(remaining - running) }
      else { result[ym] = round2(avg); running += round2(avg) }
    })
    return result
  }

  function computeProposal(method, remaining, selectedMonths, extra) {
    if (selectedMonths.length === 0 || remaining <= 0) return {}
    switch (method) {
      case 'linear': return distributeLinear(remaining, selectedMonths)
      case 'front_loaded': return distributeFrontLoaded(remaining, selectedMonths)
      case 'back_loaded': return distributeBackLoaded(remaining, selectedMonths)
      case 's_curve': return distributeSCurve(remaining, selectedMonths)
      case 'historical': return distributeHistorical(remaining, selectedMonths, extra?.historicalMonthlyPaid || {}) || {}
      case 'monthly_average': return distributeMonthlyAverage(remaining, selectedMonths, extra?.monthlyAverage)
      case 'manual': default: return {}  // manual = no proposal, user types into the existing per-cell inputs directly
    }
  }

  // Opens the SAME distribution modal, but in "period change review" mode.
  // Pure computation only — no state/database writes happen until the user
  // explicitly Applies (state only) and then Saves (database). Per the
  // locked rule: preserve overlapping months exactly, delete removed
  // months, and redistribute ONLY the releasedAmount (sum of what was in
  // the removed months) — never the full current Remaining — across the
  // newly added months, using the item's own previously saved
  // distribution_method (read-only here; never changed by this path).
  function openPeriodReviewModal(node) {
    const itemId = node.code
    const itemDist = dist[itemId] || {}
    const oldMonths = Object.keys(itemDist)
    const newMonths = months
    const preserved = oldMonths.filter(m => newMonths.includes(m))
    const removed = oldMonths.filter(m => !newMonths.includes(m))
    const added = newMonths.filter(m => !oldMonths.includes(m))
    const releasedAmount = removed.reduce((s, m) => s + (parseFloat(itemDist[m]) || 0), 0)
    const savedMethod = itemDistMeta[itemId]?.distribution_method || 'linear'
    setDistModal({
      itemId,
      itemLabel: node.description || node.name || node.code,
      isIndirect: false,  // display-only label in this mode; method is fixed, not user-selectable
      isPeriodChangeReview: true,
      method: savedMethod,
      preservedMonths: preserved,
      preservedAmounts: preserved.reduce((acc, m) => { acc[m] = itemDist[m]; return acc }, {}),
      removedMonths: removed,
      removedAmounts: removed.reduce((acc, m) => { acc[m] = itemDist[m]; return acc }, {}),
      addedMonths: added,
      releasedAmount,
      remaining: releasedAmount,  // reused by the shared proposal preview table below
      selectedMonths: added,
      monthlyAverage: '',
      historicalMonthlyPaid: {},
    })
  }

  // Collects every leaf Sub Code CODE under a given node — used to resolve
  // Historical Payment Pattern correctly regardless of WHICH level owns
  // the Remaining. cf_payment_entries.sub_code always references a true
  // Sub Code (never a Resource/Cost Code), so when the Forecast Owner is
  // an aggregation node (Case 1/3 above), its history must be gathered
  // from all Sub Codes beneath it — the same roll-up relationship already
  // used for `paid` in computeNodeStats(), applied here to history instead.
  function collectLeafCodes(node) {
    if (isLeafNode(node)) return [node.code]
    const codes = []
    for (const res of (node.resourceRows || [])) codes.push(...collectLeafCodes(res))
    for (const sub of (node.children || [])) {
      if (sub.isUnderResource === true) continue
      codes.push(...collectLeafCodes(sub))
    }
    return codes
  }

  async function openDistributeModal(node, remaining, isIndirect, isForecastOwner) {
    const futureMonths = months  // default selection: all loaded months; user can narrow in the modal
    const leafCodes = collectLeafCodes(node)
    const historicalMonthlyPaid = await getHistoricalMonthlyPaid(leafCodes, projectId)
    setDistModal({
      itemId: node.code,
      itemLabel: node.description || node.name || node.code,
      isIndirect,
      // Monthly Average follows the ACTUAL Forecast Owner (per
      // computeNodeStats()'s own isForecastOwner — the same value already
      // driving VAT treatment, Forecast with VAT, Remaining, and the
      // "Distribute" button), never tree shape. A Cost Code that owns its
      // Remaining directly (isForecastOwner=true) is eligible here exactly
      // like a Sub Code owner would be; an aggregation-only node (a
      // Category, or a Cost Code that is merely summarizing multiple
      // owning Sub Codes) is never eligible, matching "Category is
      // aggregation only and must never be a Distribution Owner" and
      // "Cost Code only summarizes" when Sub Codes are the real owners.
      isForecastOwner,
      isPeriodChangeReview: false,
      remaining,
      method: 'linear',
      selectedMonths: futureMonths,
      monthlyAverage: '',
      historicalMonthlyPaid,
    })
  }


  // nodes (per isLeafNode()) — the actual Cash Out distributable rows —
  // flattened for the purposes that don't need the hierarchy (Save/
  // Upload/Template). The RENDER path below walks the tree directly to
  // preserve the Cost Code → Resource Code → Sub Code display (Direct) or
  // Cost Code → Sub Code display (Indirect). Recursive to handle any depth.
  // Same isUnderResource skip as computeNodeStats()/renderNode() — a Sub
  // Code already reachable via resourceRows[].children[] must not also be
  // visited (and thus listed/saved) a second time via node.children[].
  //
  // Uses isDistributionOwner() — NOT isLeafNode() — because an Aggregation
  // node (Cost Code or Resource Code with children) can itself be the
  // actual Distribution Owner when it owns the Forecast directly. Using
  // isLeafNode() here was the root cause of saved distributions silently
  // disappearing: saveAll() only ever persisted items in this list, so an
  // owning Cost Code's distribution was never written to
  // cashflow_monthly_distribution at all, regardless of being marked
  // dirty. Once an ancestor is claimed as the owner, its descendants are
  // never independently distributable for the same Forecast (prevents
  // duplicate distribution across levels).
  function flattenDistributable(tree) {
    const out = []
    function visit(node, parentIsDistributionOwner) {
      const owner = isDistributionOwner(node, parentIsDistributionOwner)
      if (owner) { out.push(node); return }
      const childOwnerFlag = parentIsDistributionOwner || owner
      for (const res of (node.resourceRows || [])) visit(res, childOwnerFlag)
      for (const sub of (node.children || [])) {
        if (sub.isUnderResource === true) continue
        visit(sub, childOwnerFlag)
      }
    }
    for (const parent of tree) visit(parent, false)
    return out
  }

  async function saveAll() {
    setSaving(true)
    const allItems = [...flattenDistributable(directTree), ...flattenDistributable(indirectTree)]
    const itemsActuallySaved = new Set()
    for (const item of allItems) {
      const itemId = item.code
      const itemDist = dist[itemId] || {}
      for (const ym of months) {
        const key = `${itemId}|${ym}`
        if (!dirty[key]) continue
        const amt = itemDist[ym] || 0
        const period = firstOfMonth(ym)
        const { data: existing } = await supabase.from('cashflow_monthly_distribution')
          .select('id').eq('project_id', projectId).eq('item_type', ITEM_TYPE)
          .eq('item_id', itemId).eq('period_month', period).maybeSingle()
        if (amt === 0) {
          // Month no longer part of the allocation (removed by Period
          // Change, or replaced away by Manual Distribute) — delete the
          // row entirely rather than leaving a stale zero-amount row.
          if (existing) await supabase.from('cashflow_monthly_distribution').delete().eq('id', existing.id)
        } else if (existing) {
          await supabase.from('cashflow_monthly_distribution').update({ amount: amt }).eq('id', existing.id)
        } else {
          await supabase.from('cashflow_monthly_distribution').insert({
            project_id: projectId, item_type: ITEM_TYPE,
            item_id: itemId, item_name: item.name || itemId,
            period_month: period, amount: amt
          })
        }
        itemsActuallySaved.add(itemId)
      }
    }
    // Persist per-item distribution metadata — ONLY for items with a
    // successful save this round, and ONLY now that the database write
    // above has actually completed. Never done on load, Review, Preview,
    // or Apply alone.
    const currentPeriodStart = settings?.project_start_month
    const currentPeriodEnd = settings?.project_end_month
    for (const itemId of itemsActuallySaved) {
      const pending = pendingDistMeta[itemId]
      if (!pending) continue
      if (pending.periodOnly) {
        // Period Change recalculation: distribution_method is read-only here.
        await persistItemDistMeta(itemId, { periodStart: currentPeriodStart, periodEnd: currentPeriodEnd })
      } else if (pending.method) {
        // Manual Distribute: persists the user's explicit method choice AND
        // marks this item current for this exact period.
        await persistItemDistMeta(itemId, { distributionMethod: pending.method, periodStart: currentPeriodStart, periodEnd: currentPeriodEnd })
      }
    }
    setPendingDistMeta(p => {
      const next = { ...p }
      for (const itemId of itemsActuallySaved) delete next[itemId]
      return next
    })
    setDirty({})
    setSaving(false)
  }

  function handleUpload(e) {
    const file = e.target.files[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const wb = XLSX.read(ev.target.result, { type: 'binary' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const aoa = XLSX.utils.sheet_to_json(ws, { header:1, defval: null })
      const hdrs = aoa[0] || []
      const newDist = { ...dist }
      const newDirty = { ...dirty }
      for (let i = 1; i < aoa.length; i++) {
        const row = aoa[i]; if (!row[0]) continue
        const itemId = String(row[0]).trim()
        if (!newDist[itemId]) newDist[itemId] = {}
        for (let j = 1; j < hdrs.length; j++) {
          const ym = hdrs[j] ? String(hdrs[j]).slice(0,7) : null
          if (!ym) continue
          const amt = parseFloat(row[j]) || 0
          newDist[itemId][ym] = amt
          newDirty[`${itemId}|${ym}`] = true
        }
      }
      setDist(newDist)
      setDirty(newDirty)
    }
    reader.readAsBinaryString(file)
  }

  function downloadTemplate() {
    const allItems = [...flattenDistributable(directTree), ...flattenDistributable(indirectTree)]
    const hdr = ['Item ID (Sub Code / Resource Code — do not edit)', 'Item Name', ...months]
    const rows = allItems.map(it => [it.code, it.name || it.code, ...months.map(ym => dist[it.code]?.[ym] || 0)])
    const ws = XLSX.utils.aoa_to_sheet([hdr, ...rows])
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Distribution')
    XLSX.writeFile(wb, 'cash_out_forecast_template.xlsx')
  }

  const thS = { padding: '8px 10px', fontSize: 11, fontWeight: 700, background: '#1B3A6B', color: '#fff', whiteSpace: 'nowrap', textAlign: 'right', position: 'sticky', top: 0 }
  const thSL = { ...thS, textAlign: 'left', minWidth: 160 }
  const thSC = { ...thS, textAlign: 'center', minWidth: 80 }
  const tdS = { padding: '6px 8px', fontSize: 11, borderBottom: '1px solid #E5E7EB', textAlign: 'right', fontFamily: 'monospace' }
  const tdSL = { ...tdS, textAlign: 'left', fontWeight: 600, color: '#1B3A6B' }

  function toggleExpand(key) {
    setExpanded(e => ({ ...e, [key]: !e[key] }))
  }

  // Collect every expandable node's key (Category/Cost Code/Resource Code —
  // any non-distributable, non-leaf node) across BOTH trees, for one-click
  // Expand All / Collapse All. Pure traversal of the already-loaded tree —
  // no new data fetch, no Forecast recalculation.
  function collectExpandableKeys(tree, keyPrefix) {
    const keys = []
    function visit(node, prefix) {
      const nodeKey = prefix + '|' + node.code
      if (!isLeafNode(node)) {
        keys.push(nodeKey)
        for (const res of (node.resourceRows || [])) visit(res, nodeKey)
        // Skip Sub Codes already rendered under a Resource Code above —
        // must match renderNode()'s own filtering exactly, or Expand All
        // would generate keys for rows that are never actually rendered.
        for (const sub of (node.children || [])) {
          if (sub.isUnderResource === true) continue
          visit(sub, nodeKey)
        }
      }
    }
    for (const parent of tree) visit(parent, keyPrefix)
    return keys
  }

  function expandAll() {
    const allKeys = [...collectExpandableKeys(directTree, 'Direct Works'), ...collectExpandableKeys(indirectTree, 'Indirect Works')]
    const next = {}
    for (const k of allKeys) next[k] = true
    setExpanded(next)
  }
  function collapseAll() {
    setExpanded({})
  }

  // ── Performance: pre-compute every node's forecast/paid/monthly totals
  // ONCE per (tree, dist, paidMap, months) change, in a single bottom-up
  // pass — instead of the previous approach of re-walking the whole subtree
  // recursively for every row on every render (and AGAIN per month on top
  // of that). The recursion/aggregation RULES are byte-for-byte identical
  // to before (isDistributable → own resolvedEac/paidMap/dist; otherwise →
  // sum of resourceRows + children) — only the WHEN or how many TIMES it
  // runs has changed. Keyed by `code` (same key used for dist/paidMap
  // throughout), so lookups during render are O(1) instead of a full
  // subtree walk.
  // `forecast` for EVERY node — leaf or aggregation — comes directly from
  // node.resolvedEac exactly as resolveCurrentForecastState() already
  // computed it (per the SSOT rule: Sub-level forecast already rolled up
  // into the Resource/Cost Code's own resolvedEac by resolveResource()/
  // resolveParent()). This function NEVER re-sums children into a
  // "forecast" total of its own — that would be duplicated Forecast
  // aggregation logic living outside Cost Control, which is exactly what
  // must not exist here. The only things actually computed here are Paid
  // (from cf_payment_entries, an independent actuals source) and the Cash
  // Out monthly distribution totals (this page's own, and only, data).
  // ── FIX: Direct Sub Code double-counting ──────────────────────────────
  // Root cause (proven via runtime debug, real data, not theoretical):
  // buildParentRow() in buildCostControlData.js attaches EVERY Direct Sub
  // Code under a Cost Code to TWO places on the same parent object:
  //   1. resourceRows[].children[]  — the correct Resource-owned Sub Codes
  //   2. parent.children[]          — ALL Sub Codes again, flagged with
  //                                   isUnderResource: true/false
  // (see buildParentRow()'s `subRowsWithGrouping`, line ~845). This flag
  // survives into buildForecastStructure()'s output untouched (that
  // function only strips `eac`, nothing else), so it reaches
  // resolveCurrentForecastState()'s tree exactly as attached.
  //
  // The correct fix (per approved direction): do NOT deduplicate by code
  // string (a code could theoretically repeat in genuinely different
  // contexts) — use the real structural relationship already present on
  // each node. A Sub Code with isUnderResource === true is already counted
  // via its Resource Code above; walking it AGAIN through
  // node.children[] would double it. Only Sub Codes with
  // isUnderResource === false (true orphans, or any Indirect Sub Code,
  // where this flag is simply absent/undefined and therefore never
  // matches `=== true`) contribute through this path.
  //
  // This changes ONLY how CashOutForecast walks the existing tree — the
  // tree itself (buildCostControlData.js), Payment Entries, and Forecast
  // resolution are completely untouched.
  // Forecast with VAT (item 1 of this task): a Cash Out–only planning
  // adjustment layered ON TOP of the unmodified `forecast` (resolvedEac)
  // above — Current Forecast itself is never changed. VAT is applied ONLY
  // when the user has explicitly marked this item "vat_applicable"
  // (default; see vatTreatment state) using the single Project VAT Rate
  // (cashflow_settings.project_vat_rate) — never a hard-coded rate, never a
  // per-row rate. For a leaf, this is a direct multiply; for an aggregation
  // node it is the pure sum of its own already-computed children's
  // forecastWithVat (same roll-up convention as `forecast`/`paid` above —
  // never recalculated independently at the parent level).
  // Forecast with VAT — VAT must be applied at the exact node that OWNS
  // the resolved Current Forecast (per Cost Control's own resolveDraft()),
  // determined SOLELY by node.isOwner as resolveLeaf()/resolveResource()/
  // resolveParent() set it — never inferred from tree shape (leaf vs.
  // aggregation). A prior version of this code fell back to "treat every
  // leaf as an owner", which was wrong: when a Resource Code (or Cost
  // Code) owns the Forecast directly, its Sub Codes are still leaves in
  // tree-shape terms but are explicitly NOT owners (resolveResource()
  // resolves them via resolveLeaf() with no decision, giving isOwner:false
  // and a Budget-fallback resolvedEac that must never independently gain
  // its own "Forecast with VAT" or VAT-treatment control in the UI).
  //
  // Rule (matches resolveResource()/resolveParent()'s own aggregation
  // exactly, just adding a VAT multiply at the owning node instead of
  // Cost Control's plain sum):
  //   - node.isOwner === true: VAT applies HERE, directly to this node's
  //     own resolvedEac. Do NOT also descend into children for Forecast
  //     purposes — Cost Control's own resolveResource()/resolveParent()
  //     don't use child values when the parent itself is the decision
  //     owner (see the `if (decision != null) return {... , isOwner:true}`
  //     branch, which never reads resolvedResourceRows/resolvedChildren's
  //     resolvedEac at all), so descending here would independently
  //     recompute a Forecast under this exact code, which is exactly the
  //     "second Forecast engine" that must not exist.
  //   - node.isOwner === false: this node's own resolvedEac is ALREADY
  //     the sum Cost Control itself computed from children (or Budget
  //     fallback if nothing below has a Forecast) — descend and let each
  //     child apply VAT independently at ITS owning level (recursively —
  //     a non-owner child may itself have owning grandchildren, or may be
  //     a non-owner Budget-fallback leaf with no VAT control of its own),
  //     then sum the already-VAT-adjusted child amounts. This is the
  //     "3M×1.15 + 2M×1.15" case, never "5M×1.15" after the fact.
  //   - hasEffectiveForecast === false ANYWHERE below this node (i.e. no
  //     descendant, and not this node itself, has an actual Forecast
  //     decision) means resolvedEac here is pure Budget fallback — this
  //     is STILL "the valid Current Forecast" per the approved rule ("No
  //     decision = Budget/BAC fallback... this is still the valid Current
  //     Forecast"), so VAT must still apply somewhere. Since nothing below
  //     is an owner either, this node itself is the correct (and only)
  //     place to apply it — treated as the owner for VAT purposes even
  //     though isOwner is technically false. This is what was missing:
  //     the previous version required an explicit isOwner:true and left
  //     pure-Budget branches (the common case before any Forecast decision
  //     has ever been entered in Direct/Indirect Forecast) with VAT=0
  //     everywhere, which is wrong — Budget fallback is still a real,
  //     displayed Current Forecast value and must still get VAT treatment.
  // Resolved from project_vat_config, the single Project VAT source --
  // zero_rated/standard both carry an explicit numeric rate; exempt and
  // not_applicable correctly yield 0 here (no VAT forecast), and an
  // unconfigured project also yields 0 rather than a silent 15% guess.
  const resolvedVatRate = projectVatConfig?.vat_treatment === 'standard' || projectVatConfig?.vat_treatment === 'zero_rated'
    ? (parseFloat(projectVatConfig.vat_rate) || 0)
    : 0
  const vatRate = resolvedVatRate / 100
  // Forecast with VAT mirrors Current Forecast EXACTLY, at every level of
  // the existing Cost Control hierarchy — Cost Code, Resource Code, and
  // Sub Code alike. Wherever resolvedEac (Current Forecast) is displayed
  // for a node, Forecast with VAT is simply that same node's own
  // resolvedEac × (1 + Project VAT Rate) if VAT Applicable, or unchanged
  // if No VAT. This applies independently and identically to every node
  // regardless of whether resolvedEac came from an explicit Forecast
  // Decision or from Budget fallback, and regardless of what any
  // parent/child node's own resolvedEac happens to be — exactly mirroring
  // how Current Forecast itself is already shown independently at every
  // level without one level's value ever "cancelling" another's.
  function computeNodeStats(node, statsByCode) {
    const forecast = node.resolvedEac || 0
    let paid = 0
    const monthTotals = {}
    for (const ym of months) monthTotals[ym] = 0

    const treatment = vatTreatment[node.code] || 'vat_applicable'  // default per spec ("some items VAT applicable, some not" — VAT-applicable is the safer default; salaries etc. are explicitly opted out)
    const forecastWithVat = treatment === 'vat_applicable' ? forecast * (1 + vatRate) : forecast

    // Every node reads its own dist[node.code] directly for its monthly
    // distribution — there is no ownership suppression anymore. Paid still
    // also rolls up from children below (unchanged) so an expanded parent
    // row's Paid reflects its full subtree, matching the existing display
    // convention used throughout this page.
    const nodeDist = dist[node.code] || {}
    for (const ym of months) monthTotals[ym] = nodeDist[ym] || 0

    if (isLeafNode(node)) {
      paid = paidMap[(node.code || '').toLowerCase()] || 0
    } else {
      for (const res of (node.resourceRows || [])) {
        const s = computeNodeStats(res, statsByCode)
        paid += s.paid
      }
      for (const sub of (node.children || [])) {
        // Skip: already counted once via resourceRows[].children[] above.
        if (sub.isUnderResource === true) continue
        const s = computeNodeStats(sub, statsByCode)
        paid += s.paid
      }
    }
    // Every node now has its own independent Forecast with VAT, so
    // Remaining is simply that node's own forecastWithVat − paid,
    // everywhere, with no "—" suppression case anymore.
    const remaining = forecastWithVat - paid
    const stats = { forecast, paid, forecastWithVat, remaining, monthTotals, isForecastOwner: true }
    statsByCode[node.code] = stats
    return stats
  }

  // Recomputes only when one of these actually changes (a single cell edit
  // changes `dist` — the whole tree structure/months/paidMap do not — so
  // typing in one input recomputes this once, not on every keystroke twice
  // over via duplicate recursive calls as before).
  const directStats = useMemo(() => {
    const map = {}
    for (const parent of directTree) computeNodeStats(parent, map)
    return map
  }, [directTree, dist, paidMap, months, vatTreatment, projectVatConfig?.vat_rate, projectVatConfig?.vat_treatment])

  const indirectStats = useMemo(() => {
    const map = {}
    for (const parent of indirectTree) computeNodeStats(parent, map)
    return map
  }, [indirectTree, dist, paidMap, months, vatTreatment, projectVatConfig?.vat_rate, projectVatConfig?.vat_treatment])

  // One row renderer for any node at any depth. Distributable nodes get an
  // editable monthly row (Sub Code owning its own forecast, or a Resource/
  // Sub Code with nothing more specific forecasted beneath it); every other
  // node is a collapsible aggregation row — exactly Cost Control's own
  // parent/child rendering convention (see CostControl.jsx's toggleExpand).
  // `statsMap` is the pre-computed table from above — no recursion or
  // recalculation happens here, only lookups.
  function renderNode(node, depth, keyPrefix, statsMap, isIndirect, parentIsDistributionOwner) {
    const nodeKey = keyPrefix + '|' + node.code
    const stats = statsMap[node.code] || { forecast: 0, paid: 0, forecastWithVat: 0, remaining: 0, monthTotals: {} }
    const forecast = stats.forecast
    // Same Distribution Owner rule used by flattenDistributable()/saveAll()
    // — the Distribute button and Check must only ever appear on the node
    // that actually owns the distribution, never on every displayed row.
    const owner = isDistributionOwner(node, parentIsDistributionOwner)
    const childDistOwnerFlag = parentIsDistributionOwner || owner

    if (isLeafNode(node)) {
      const itDist = dist[node.code] || {}
      const totalMonths = months.reduce((s,ym) => s+(itDist[ym]||0), 0)
      // Check must compare the monthly distribution against the SAME
      // node's Remaining — not Current Forecast. Distribute always writes
      // into dist[node.code] for whichever node the user actually
      // distributed on (Cost Code, Resource Code, or Sub Code — every
      // node is independent now), and Remaining (Forecast with VAT − Paid)
      // is the actual amount available to distribute at that exact node.
      // Comparing against `forecast` (pre-VAT Current Forecast) was wrong:
      // it ignored both VAT and Paid, so the Check never reconciled to
      // zero even when the distribution correctly summed to the full
      // Remaining amount.
      const variance = totalMonths - stats.remaining
      const varColor = Math.abs(variance) < 1 ? '#16a34a' : variance > 0 ? '#dc2626' : '#d97706'
      const treatment = vatTreatment[node.code] || 'vat_applicable'
      const remainingColor = stats.remaining != null && stats.remaining < 0 ? '#dc2626' : '#16a34a'
      return (
        <tr key={nodeKey} style={{ background: '#fff' }}
          onMouseEnter={e => e.currentTarget.style.background='#F0F4FF'}
          onMouseLeave={e => e.currentTarget.style.background='#fff'}>
          <td style={{ ...tdSL, paddingLeft: 14 + depth*18 }}>
            <span style={{ fontFamily: 'monospace' }}>{node.code}</span>
            <span style={{ display: 'block', fontSize: 9, fontWeight: 400, color: '#9CA3AF' }}>{node.description || node.name || ''}</span>
          </td>
          <td style={{ ...tdS, fontWeight: 700, color: '#1B3A6B' }} title="Current Forecast — from Cost Control, read-only">{fmt(forecast)}</td>
          <td style={{ padding: '4px 8px', borderBottom: '1px solid #E5E7EB' }}>
            <select value={treatment} onChange={e => setItemVatTreatment(node.code, e.target.value)}
              style={{ fontSize: 10, padding: '2px 4px', border: '1px solid #E5E7EB', borderRadius: 4, width: '100%' }}>
              <option value="vat_applicable">VAT Applicable</option>
              <option value="no_vat">No VAT</option>
            </select>
          </td>
          <td style={{ ...tdS, fontWeight: 700, color: '#0369A1' }} title={`Current Forecast × (1 + Project VAT Rate ${resolvedVatRate}%) — used for Cash Out planning only, never fed back into Cost Control`}>{fmt(stats.forecastWithVat)}</td>
          <td style={tdS}>{fmt(stats.paid)}</td>
          <td style={{ ...tdS, fontWeight: 700, color: remainingColor }} title="Forecast with VAT − Paid">
            {fmt(stats.remaining)}{stats.remaining < 0 ? ' (overpaid)' : ''}
            {owner && stats.remaining > 0 && (
              <button onClick={() => openDistributeModal(node, stats.remaining, isIndirect, true)}
                style={{ display:'block', marginTop:2, fontSize:9, padding:'1px 6px', border:'1px solid #1B3A6B', borderRadius:4, background:'#fff', color:'#1B3A6B', cursor:'pointer' }}>
                Distribute
              </button>
            )}
            {owner && periodChangeFlags[node.code] && (
              <button onClick={() => openPeriodReviewModal(node)}
                style={{ display:'block', marginTop:2, fontSize:9, padding:'1px 6px', border:'1px solid #EAB308', borderRadius:4, background:'#FEF9C3', color:'#854D0E', cursor:'pointer' }}>
                ⚠ Period Changed — Review
              </button>
            )}
          </td>
          {months.map(ym => (
            <td key={ym} style={{ padding: 2, borderBottom: '1px solid #E5E7EB' }}>
              {owner ? (
                <input
                  type="number"
                  value={itDist[ym] || ''}
                  onChange={e => handleCell(node.code, ym, e.target.value)}
                  style={{ width: 80, padding: '4px 6px', fontSize: 11, border: dirty[`${node.code}|${ym}`] ? '1px solid #1B3A6B' : '1px solid #E5E7EB', borderRadius: 4, textAlign: 'right', fontFamily: 'monospace', background: dirty[`${node.code}|${ym}`] ? '#EFF6FF' : '#fff' }}
                />
              ) : (
                // Not the Distribution Owner (a parent Resource/Cost Code
                // owns and distributes for this Forecast instead) — no
                // independent monthly input here, matching
                // isDistributionOwner()/flattenDistributable() exactly.
                <span style={{ fontSize: 10, color: '#9CA3AF' }}>—</span>
              )}
            </td>
          ))}
          <td style={{ ...tdS, fontWeight: 700, color: owner ? varColor : '#9CA3AF', minWidth: 90 }} title="SUM(monthly distribution) − Remaining (Forecast with VAT − Paid) — must reach zero">
            {owner ? (Math.abs(variance) < 1 ? '✓ OK' : fmt(variance)) : '—'}
          </td>
        </tr>
      )
    }

    // Aggregation/display row (Cost Code, Resource Code with owning Sub
    // Codes beneath it, or Cost Code Category) — collapsible, no input.
    // Collapsed nodes still need their OWN totals (already O(1) from
    // statsMap above), but we skip rendering/recursing into their children
    // entirely — no per-child row components are created while collapsed.
    const isExpanded = expanded[nodeKey] === true  // default COLLAPSED — lighter initial load, per requirement
    // Unlike a leaf, an aggregation row (Cost Code / Resource Code with
    // children) ALWAYS has a real forecastWithVat/remaining to show —
    // either because it owns the Forecast directly (isForecastOwner=true,
    // VAT applied once right here) or because it is the pure roll-up of
    // its Forecast-owning descendants (isForecastOwner=false, sum of
    // already-VAT-adjusted children). Only a NON-owning LEAF (a true
    // dead-end with no Forecast of its own — the "Sub Code under an
    // owning Resource/Cost Code" case) shows "—", per the approved rule.
    const remainingColorAgg = stats.remaining < 0 ? '#dc2626' : '#16a34a'
    const aggTreatment = vatTreatment[node.code] || 'vat_applicable'
    return (
      <Fragment key={nodeKey}>
        <tr onClick={() => toggleExpand(nodeKey)} style={{ background: depth === 0 ? '#EEF0F3' : '#F5F6F8', cursor: 'pointer' }}>
          <td style={{ ...tdSL, paddingLeft: 14 + depth*18, fontWeight: 700 }}>
            <span style={{ fontSize: 10, color: '#1A7A4A', marginRight: 4 }}>{isExpanded ? '▼' : '▶'}</span>
            <span style={{ fontFamily: 'monospace' }}>{node.code}</span>
            {' '}<span style={{ color: '#6B7280', fontWeight: 400 }}>{node.description || node.name || ''}</span>
          </td>
          <td style={{ ...tdS, fontWeight: 700, color: '#1B3A6B' }}>{fmt(forecast)}</td>
          <td onClick={e => e.stopPropagation()} style={{ padding: '4px 8px', borderBottom: '1px solid #E5E7EB' }}>
            <select value={aggTreatment} onChange={e => setItemVatTreatment(node.code, e.target.value)}
              style={{ fontSize: 10, padding: '2px 4px', border: '1px solid #E5E7EB', borderRadius: 4, width: '100%' }}>
              <option value="vat_applicable">VAT Applicable</option>
              <option value="no_vat">No VAT</option>
            </select>
          </td>
          <td style={{ ...tdS, fontWeight: 700, color: '#0369A1' }}>{fmt(stats.forecastWithVat)}</td>
          <td style={{ ...tdS, fontWeight: 700 }}>{fmt(stats.paid)}</td>
          <td style={{ ...tdS, fontWeight: 700, color: remainingColorAgg }}>
            {fmt(stats.remaining)}
            {owner && stats.remaining > 0 && (
              <button onClick={e => { e.stopPropagation(); openDistributeModal(node, stats.remaining, isIndirect, true) }}
                style={{ display:'block', marginTop:2, fontSize:9, padding:'1px 6px', border:'1px solid #1B3A6B', borderRadius:4, background:'#fff', color:'#1B3A6B', cursor:'pointer' }}>
                Distribute
              </button>
            )}
            {owner && periodChangeFlags[node.code] && (
              <button onClick={e => { e.stopPropagation(); openPeriodReviewModal(node) }}
                style={{ display:'block', marginTop:2, fontSize:9, padding:'1px 6px', border:'1px solid #EAB308', borderRadius:4, background:'#FEF9C3', color:'#854D0E', cursor:'pointer' }}>
                ⚠ Period Changed — Review
              </button>
            )}
          </td>
          {months.map(ym => {
            const t = stats.monthTotals[ym] || 0
            return <td key={ym} style={{ ...tdS, fontWeight: 700 }}>{t ? fmt(t) : '—'}</td>
          })}
          {(() => {
            // Same Check rule as the leaf row: SUM(this exact node's own
            // dist[node.code]) − this exact node's own Remaining — shown
            // ONLY when this node is the actual Distribution Owner
            // (isDistributionOwner), matching flattenDistributable()/
            // saveAll() exactly. A pure summary Cost Code/Resource Code
            // (owner=false, rolling up owning descendants) shows "—" here
            // — its Check is meaningless since it never independently
            // writes to cashflow_monthly_distribution; each owning
            // descendant's own Check row is the real reconciliation.
            if (!owner) {
              return <td style={{ ...tdS, color: '#9CA3AF' }}>—</td>
            }
            const nodeDist = dist[node.code] || {}
            const totalMonths = months.reduce((s,ym) => s+(nodeDist[ym]||0), 0)
            const variance = totalMonths - stats.remaining
            const varColorAgg = Math.abs(variance) < 1 ? '#16a34a' : variance > 0 ? '#dc2626' : '#d97706'
            return (
              <td style={{ ...tdS, fontWeight: 700, color: varColorAgg, minWidth: 90 }} title="SUM(monthly distribution) − Remaining (Forecast with VAT − Paid) — must reach zero">
                {Math.abs(variance) < 1 ? '✓ OK' : fmt(variance)}
              </td>
            )
          })()}
        </tr>
        {isExpanded && (node.resourceRows || []).map(res => renderNode(res, depth+1, nodeKey, statsMap, isIndirect, childDistOwnerFlag))}
        {/* Skip rendering Sub Codes already shown under a Resource Code above
            — same duplicate-representation fix as computeNodeStats(). Without
            this, the same Sub Code row would visually appear twice on screen
            even though the underlying Paid figure is now correctly counted
            once. */}
        {isExpanded && (node.children || []).filter(sub => sub.isUnderResource !== true).map(sub => renderNode(sub, depth+1, nodeKey, statsMap, isIndirect, childDistOwnerFlag))}
      </Fragment>
    )
  }

  function renderSection(label, tree, statsMap, isIndirect) {
    const totForecast = tree.reduce((s,p) => s+(statsMap[p.code]?.forecast || 0), 0)
    const totForecastWithVat = tree.reduce((s,p) => s+(statsMap[p.code]?.forecastWithVat || 0), 0)
    const totPaid      = tree.reduce((s,p) => s+(statsMap[p.code]?.paid || 0), 0)
    const totRemainingBalance = tree.reduce((s,p) => s+(statsMap[p.code]?.remaining || 0), 0)  // Forecast with VAT − Paid
    const totMonthVariance = totForecast - tree.reduce((s,p) => s+months.reduce((s2,ym)=>s2+(statsMap[p.code]?.monthTotals[ym]||0),0), 0)

    return (
      <>
        <tr>
          <td colSpan={6 + months.length} style={{ padding: '8px 10px', background: '#1B3A6B', color: '#fff', fontWeight: 700, fontSize: 12 }}>
            {label}
          </td>
        </tr>
        {tree.map(parent => renderNode(parent, 0, label, statsMap, isIndirect, false))}
        <tr style={{ background: '#E8EDF5', fontWeight: 700 }}>
          <td style={{ ...tdSL, color: '#1B3A6B' }}>Total {label}</td>
          <td style={tdS}>{fmt(totForecast)}</td>
          <td style={tdS} />
          <td style={{ ...tdS, color: '#0369A1' }}>{fmt(totForecastWithVat)}</td>
          <td style={tdS}>{fmt(totPaid)}</td>
          <td style={{ ...tdS, color: totRemainingBalance < 0 ? '#dc2626' : '#16a34a' }}>{fmt(totRemainingBalance)}</td>
          {months.map(ym => {
            const total = tree.reduce((s,p) => s+(statsMap[p.code]?.monthTotals[ym] || 0), 0)
            return <td key={ym} style={{ ...tdS, fontWeight: 700 }}>{total ? fmt(total) : '—'}</td>
          })}
          <td style={{ ...tdS, fontWeight: 700, color: Math.abs(totMonthVariance) < 1 ? '#16a34a' : '#6B7280' }}>
            {Math.abs(totMonthVariance) >= 1 ? fmt(totMonthVariance) + ' unalloc.' : ''}
          </td>
        </tr>
      </>
    )
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Loading...</div>

  if (!settings?.project_start_month) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <p style={{ color: '#6B7280', marginBottom: 16 }}>حدد أولاً فترة المشروع لتظهر الشهور</p>
      <button onClick={() => setShowSettings(true)}
        style={{ padding: '10px 24px', background: '#1B3A6B', color: '#fff', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700 }}>
        ⚙ تحديد فترة المشروع
      </button>
      {showSettings && renderSettingsModal()}
    </div>
  )

  const grandTotForecast  = [...directTree,...indirectTree].reduce((s,p)=>s+(directStats[p.code]?.forecast ?? indirectStats[p.code]?.forecast ?? 0),0)
  const grandTotForecastWithVat = [...directTree,...indirectTree].reduce((s,p)=>s+(directStats[p.code]?.forecastWithVat ?? indirectStats[p.code]?.forecastWithVat ?? 0),0)
  const grandTotPaid      = [...directTree,...indirectTree].reduce((s,p)=>s+(directStats[p.code]?.paid ?? indirectStats[p.code]?.paid ?? 0),0)
  const grandTotRemaining = grandTotForecastWithVat - grandTotPaid

  function renderDistributeModal() {
    if (!distModal) return null
    const { itemId, itemLabel, isIndirect, isForecastOwner, remaining, method, selectedMonths, monthlyAverage, historicalMonthlyPaid, isPeriodChangeReview, preservedMonths, preservedAmounts, removedMonths, removedAmounts, addedMonths, releasedAmount } = distModal
    const historyMonthCount = Object.keys(historicalMonthlyPaid || {}).length
    const proposal = computeProposal(method, remaining, selectedMonths, { historicalMonthlyPaid, monthlyAverage })
    const totalAllocated = Object.values(proposal).reduce((s,v) => s+v, 0)
    const unallocated = round2(remaining - totalAllocated)
    const reconciles = Math.abs(unallocated) < 0.01

    function toggleMonth(ym) {
      setDistModal(m => ({
        ...m,
        selectedMonths: m.selectedMonths.includes(ym) ? m.selectedMonths.filter(x => x !== ym) : [...m.selectedMonths, ym].sort(),
      }))
    }

    async function confirmApply() {
      if (isPeriodChangeReview) {
        // PERIOD CHANGE — Preserve + Release + Redistribute only. Overlapping
        // months keep their EXACT existing values (never touched); removed
        // months are zeroed out (marked dirty so the main Save performs the
        // actual DELETE); newly added months receive the proposal computed
        // from releasedAmount only — never the full current Remaining.
        setDist(d => {
          const next = { ...(d[itemId] || {}) }
          for (const m of removedMonths) delete next[m]  // preserved values for the OTHER months stay exactly as-is
          for (const ym of Object.keys(proposal)) next[ym] = proposal[ym]
          return { ...d, [itemId]: next }
        })
        setDirty(d => {
          const next = { ...d }
          for (const m of removedMonths) next[`${itemId}|${m}`] = true   // marks for deletion at Save time
          for (const ym of Object.keys(proposal)) next[`${itemId}|${ym}`] = true
          return next
        })
      } else {
        // MANUAL DISTRIBUTE — full Replace. Every previously existing month
        // for this item is cleared and marked dirty (so Save deletes any row
        // no longer part of the new allocation), then the new proposal is
        // written in full. Never merges on top of the old allocation.
        setDist(d => {
          const oldMonths = Object.keys(d[itemId] || {})
          const next = {}
          for (const ym of Object.keys(proposal)) next[ym] = proposal[ym]
          return { ...d, [itemId]: next }
        })
        setDirty(d => {
          const next = { ...d }
          const oldMonths = Object.keys(dist[itemId] || {})
          for (const ym of oldMonths) next[`${itemId}|${ym}`] = true       // covers removed-by-replace months for deletion
          for (const ym of Object.keys(proposal)) next[`${itemId}|${ym}`] = true
          return next
        })
      }
      // Records which items had a distribution-affecting Apply this session
      // so saveAll() knows to persist the right item-level metadata (method
      // + period for Manual; period only for Period Change) once the main
      // Save actually succeeds — never before.
      setPendingDistMeta(p => ({ ...p, [itemId]: isPeriodChangeReview ? { periodOnly: true } : { method } }))
      setDistModal(null)
    }

    return (
      <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50 }}>
        <div style={{ background:'#fff',borderRadius:16,padding:28,width:560,maxHeight:'85vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.2)' }}>
          <h3 style={{ fontSize:15,fontWeight:700,marginBottom:4 }}>
            {isPeriodChangeReview ? 'Cash Flow Period Changed — Review' : 'Distribute Remaining'} — {itemId}
          </h3>
          <p style={{ fontSize:11,color:'#6B7280',marginBottom:16 }}>{itemLabel}</p>

          {isPeriodChangeReview ? (
            <>
              <div style={{ background:'#FEF9C3', border:'1px solid #EAB308', borderRadius:8, padding:'10px 12px', marginBottom:16, fontSize:11 }}>
                The project's Cash Flow period changed since this item's distribution was last saved.
                Overlapping months are preserved exactly. Removed months release their amount, which is
                redistributed only across the newly added months using this item's saved method (<b>{method}</b>).
              </div>
              <div style={{ display:'flex', gap:16, marginBottom:12, fontSize:12 }}>
                <div><span style={{ color:'#6B7280' }}>Released Amount: </span><b style={{ color:'#16a34a' }}>{fmt(releasedAmount)}</b></div>
                <div><span style={{ color:'#6B7280' }}>New months: </span><b>{addedMonths.length}</b></div>
              </div>
              {removedMonths.length > 0 && (
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:11,color:'#6B7280',display:'block',marginBottom:4 }}>Removed (no longer in period)</label>
                  <div style={{ fontSize:11, color:'#dc2626' }}>
                    {removedMonths.map(m => `${monthLabel(m)}: ${fmt(removedAmounts[m])}`).join(' · ')}
                  </div>
                </div>
              )}
              {preservedMonths.length > 0 && (
                <div style={{ marginBottom:12 }}>
                  <label style={{ fontSize:11,color:'#6B7280',display:'block',marginBottom:4 }}>Preserved (unchanged)</label>
                  <div style={{ fontSize:11, color:'#16a34a' }}>
                    {preservedMonths.map(m => `${monthLabel(m)}: ${fmt(preservedAmounts[m])}`).join(' · ')}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ display:'flex', gap:16, marginBottom:16, fontSize:12 }}>
              <div><span style={{ color:'#6B7280' }}>Remaining: </span><b style={{ color:'#16a34a' }}>{fmt(remaining)}</b></div>
              <div><span style={{ color:'#6B7280' }}>Selected months: </span><b>{selectedMonths.length}</b></div>
            </div>
          )}

          {!isPeriodChangeReview && (
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:11,color:'#6B7280',display:'block',marginBottom:6 }}>Method</label>
              <select value={method} onChange={e => setDistModal(m => ({ ...m, method: e.target.value }))}
                style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 10px', fontSize:13 }}>
                <option value="linear">Linear — equal across selected months</option>
                <option value="manual">Manual — edit cells directly (no proposal)</option>
                <option value="front_loaded">Front-loaded — higher early, decreasing</option>
                <option value="back_loaded">Back-loaded — lower early, increasing</option>
                <option value="s_curve">S-Curve — gradual start/end, peak middle</option>
                <option value="historical" disabled={historyMonthCount < 3}>
                  Historical Payment Pattern {historyMonthCount < 3 ? `(needs ≥3 months, found ${historyMonthCount})` : `(${historyMonthCount} months of history)`}
                </option>
                {isIndirect && isForecastOwner && <option value="monthly_average">Monthly Average (Indirect Forecast Owner only)</option>}
              </select>
            </div>
          )}

          {!isPeriodChangeReview && method === 'monthly_average' && (
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:11,color:'#6B7280',display:'block',marginBottom:6 }}>Monthly Average Amount</label>
              <input type="number" value={monthlyAverage}
                onChange={e => setDistModal(m => ({ ...m, monthlyAverage: e.target.value }))}
                style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 10px', fontSize:13 }}/>
              <p style={{ fontSize:10, color:'#9CA3AF', marginTop:4 }}>The final selected month is adjusted automatically so the total still equals Remaining exactly.</p>
            </div>
          )}

          {!isPeriodChangeReview && (
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:11,color:'#6B7280',display:'block',marginBottom:6 }}>Months</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4, maxHeight:120, overflowY:'auto', border:'1px solid #E5E7EB', borderRadius:8, padding:8 }}>
                {months.map(ym => (
                  <button key={ym} onClick={() => toggleMonth(ym)}
                    style={{ fontSize:10, padding:'3px 8px', borderRadius:6, border: selectedMonths.includes(ym) ? '1px solid #1B3A6B' : '1px solid #E5E7EB',
                      background: selectedMonths.includes(ym) ? '#1B3A6B' : '#fff', color: selectedMonths.includes(ym) ? '#fff' : '#374151', cursor:'pointer' }}>
                    {monthLabel(ym)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(isPeriodChangeReview || method !== 'manual') && (
            <div style={{ marginBottom:16, border:'1px solid #E5E7EB', borderRadius:8, overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                <thead>
                  <tr style={{ background:'#F3F4F6' }}>
                    <th style={{ textAlign:'left', padding:'6px 10px' }}>Month</th>
                    <th style={{ textAlign:'right', padding:'6px 10px' }}>{isPeriodChangeReview ? 'New Allocation' : 'Proposed Amount'}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedMonths.map(ym => (
                    <tr key={ym}>
                      <td style={{ padding:'4px 10px' }}>{monthLabel(ym)}</td>
                      <td style={{ padding:'4px 10px', textAlign:'right', fontFamily:'monospace' }}>{fmt(proposal[ym] || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding:'8px 10px', background:'#F9FAFB', display:'flex', justifyContent:'space-between', fontWeight:700 }}>
                <span>Total Allocated: {fmt(totalAllocated)}</span>
                <span style={{ color: reconciles ? '#16a34a' : '#dc2626' }}>
                  {reconciles ? `✓ Reconciles to ${isPeriodChangeReview ? 'Released Amount' : 'Remaining'}` : `Unallocated: ${fmt(unallocated)}`}
                </span>
              </div>
            </div>
          )}

          {isPeriodChangeReview && addedMonths.length === 0 && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FCA5A5', borderRadius:8, padding:'10px 12px', marginBottom:16, fontSize:11, color:'#991B1B' }}>
              No new future months are available to receive the released amount ({fmt(releasedAmount)}).
              Removing the old months will leave this amount unallocated — the Check will show this imbalance.
            </div>
          )}

          <div style={{ display:'flex',gap:10 }}>
            <button onClick={() => setDistModal(null)}
              style={{ flex:1,padding:'10px',border:'1px solid #E5E7EB',borderRadius:8,cursor:'pointer',background:'#fff' }}>Cancel</button>
            {(isPeriodChangeReview || method !== 'manual') && (
              <button onClick={confirmApply} disabled={!isPeriodChangeReview && (!reconciles || selectedMonths.length === 0)}
                style={{ flex:1,padding:'10px',background: (isPeriodChangeReview || reconciles) ? '#1B3A6B' : '#9CA3AF',color:'#fff',borderRadius:8,border:'none',cursor: (isPeriodChangeReview || reconciles) ? 'pointer':'not-allowed',fontWeight:700 }}>
                Apply to Cells
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  function renderSettingsModal() {
    return (
      <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:50 }}>
        <div style={{ background:'#fff',borderRadius:16,padding:32,width:380,boxShadow:'0 20px 60px rgba(0,0,0,.2)' }}>
          <h3 style={{ fontSize:16,fontWeight:700,marginBottom:20 }}>⚙ فترة المشروع</h3>
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12,color:'#6B7280',display:'block',marginBottom:6 }}>شهر البداية</label>
            <input type="month" value={settingsForm.project_start_month}
              onChange={e => setSettingsForm(p=>({...p,project_start_month:e.target.value}))}
              style={{ width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:'8px 12px',fontSize:13 }}/>
          </div>
          <div style={{ marginBottom:24 }}>
            <label style={{ fontSize:12,color:'#6B7280',display:'block',marginBottom:6 }}>شهر النهاية</label>
            <input type="month" value={settingsForm.project_end_month}
              onChange={e => setSettingsForm(p=>({...p,project_end_month:e.target.value}))}
              style={{ width:'100%',border:'1px solid #E5E7EB',borderRadius:8,padding:'8px 12px',fontSize:13 }}/>
          </div>
          {/* Project VAT Rate input removed: this page now reads the
              rate from project_vat_config (the single Project VAT
              source), not a page-local setting. The rate itself is
              configured at the project level, outside this modal. */}
          <div style={{ display:'flex',gap:10 }}>
            <button onClick={() => setShowSettings(false)}
              style={{ flex:1,padding:'10px',border:'1px solid #E5E7EB',borderRadius:8,cursor:'pointer',background:'#fff' }}>إلغاء</button>
            <button onClick={saveSettings}
              style={{ flex:1,padding:'10px',background:'#1B3A6B',color:'#fff',borderRadius:8,border:'none',cursor:'pointer',fontWeight:700 }}>حفظ</button>
          </div>
        </div>
      </div>
    )
  }

  // Reconciliation summary for the alarm panel below — grouped by sub_code,
  // reading only from `unmatchedPaid.rows` (already computed in loadForecast()
  // by checking every Payment Entry's sub_code against the actual, currently
  // loaded Cost Control tree — no separate hierarchy, no re-query, no fuzzy
  // matching beyond the same trim/lowercase normalization used everywhere
  // else in this file for sub_code lookups).
  const unmatchedGrouped = Object.values(
    unmatchedPaid.rows.reduce((acc, r) => {
      const code = r.sub_code || '(blank)'
      if (!acc[code]) acc[code] = { sub_code: code, count: 0, total: 0 }
      acc[code].count += 1
      acc[code].total += parseFloat(r.amount_inc_vat) || 0
      return acc
    }, {})
  ).sort((a, b) => b.total - a.total)
  const matchedPaidTotal = grandTotPaid  // SUM of every Paid value shown in the tree above
  const paymentEntriesTotalChecked = matchedPaidTotal + unmatchedPaid.total

  return (
    <div>
      {/* ── Payment Entry / Cost Control Sub Code reconciliation — a
          verification layer only. It never alters Paid, never excludes an
          entry from the Paid total, never reassigns an amount to another
          node, and never fabricates a Cost Code/Sub Code to absorb the
          difference. Matched entries flow into the tree above exactly as
          before; unmatched entries stay counted in Paid's grand total but
          are surfaced here so the underlying data can be corrected. ── */}
      {unmatchedGrouped.length === 0 ? (
        <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 11, color: '#166534' }}>
          ✓ Payment Entries and Cost Control Sub Codes are fully matched. {fmt(paymentEntriesTotalChecked)} SAR checked across all entries — 0 unmatched.
        </div>
      ) : (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 12, color: '#991B1B' }}>
          <div style={{ fontWeight: 800, marginBottom: 4 }}>🔴 Payment Entry / Cost Control Mapping Error</div>
          <div style={{ marginBottom: 8 }}>
            Some Payment Entry Sub Codes are not found in the current Cost Control tree. These amounts remain in the Paid total shown below but are not attributed to any Cost Code/Resource Code/Sub Code row — correct the underlying Sub Code to resolve this.
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 480, marginBottom: 6 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', fontSize: 11, padding: '2px 8px 2px 0' }}>Sub Code</th>
                <th style={{ textAlign: 'right', fontSize: 11, padding: '2px 8px' }}>Entries</th>
                <th style={{ textAlign: 'right', fontSize: 11, padding: '2px 0' }}>Paid Amount</th>
              </tr>
            </thead>
            <tbody>
              {unmatchedGrouped.map(g => (
                <tr key={g.sub_code}>
                  <td style={{ padding: '2px 8px 2px 0', fontFamily: 'monospace' }}>{g.sub_code}</td>
                  <td style={{ textAlign: 'right', padding: '2px 8px' }}>{g.count}</td>
                  <td style={{ textAlign: 'right', padding: '2px 0', fontFamily: 'monospace' }}>{fmt(g.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontWeight: 700 }}>Total Unmatched: {fmt(unmatchedPaid.total)} SAR</div>
        </div>
      )}

      <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 11, color: '#1E40AF' }}>
        ℹ Forecast values shown here come directly from Cost Control's Current Forecast (live) — this page only stores the monthly cash-out timing you assign. Editing a forecast amount must be done in Cost Control / Direct-Indirect Forecast.
      </div>

      <div style={{ display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center' }}>
        <button onClick={() => setShowSettings(true)}
          style={{ padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer' }}>
          ⚙ {settings?.project_start_month?.slice(0,7)} → {settings?.project_end_month?.slice(0,7)}
        </button>
        <button onClick={expandAll}
          style={{ padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer' }}>
          ⊞ Expand All
        </button>
        <button onClick={collapseAll}
          style={{ padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer' }}>
          ⊟ Collapse All
        </button>
        <button onClick={downloadTemplate}
          style={{ padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer' }}>
          ↓ Template
        </button>
        <label style={{ padding:'6px 14px',border:'1px solid #E5E7EB',borderRadius:6,background:'#fff',fontSize:12,cursor:'pointer' }}>
          ↑ Upload
          <input type="file" accept=".xlsx,.xls" onChange={handleUpload} style={{ display:'none' }}/>
        </label>
        {Object.keys(dirty).length > 0 && (
          <button onClick={saveAll} disabled={saving}
            style={{ padding:'6px 18px',background:'#1B3A6B',color:'#fff',borderRadius:6,border:'none',fontSize:12,fontWeight:700,cursor:'pointer' }}>
            {saving ? '⏳ Saving...' : `💾 Save (${Object.keys(dirty).length} changes)`}
          </button>
        )}
      </div>

      <div ref={scrollRef} onScroll={updateScrollState} style={{ overflow:'auto', maxHeight:'70vh', borderRadius:16, border:'1px solid #E5E7EB', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
        <table style={{ borderCollapse:'collapse', width:'100%', minWidth: 800 }}>
          <thead>
            <tr>
              <th style={{ ...thSL, position:'sticky',top:0,left:0,zIndex:3,minWidth:220 }}>Item (Sub Code / Resource Code)</th>
              <th style={{ ...thS, position:'sticky', top:0, zIndex:2 }}>Current Forecast</th>
              <th style={{ ...thSC, position:'sticky', top:0, zIndex:2, minWidth:110 }}>VAT Treatment</th>
              <th style={{ ...thS, position:'sticky', top:0, zIndex:2 }}>Forecast with VAT</th>
              <th style={{ ...thS, position:'sticky', top:0, zIndex:2 }}>Paid</th>
              <th style={{ ...thS, position:'sticky', top:0, zIndex:2 }}>Remaining</th>
              {months.map(ym => <th key={ym} style={{ ...thSC, position:'sticky', top:0, zIndex:2 }}>{monthLabel(ym)}</th>)}
              <th style={{ ...thSC, background:'#0F2A50', position:'sticky', top:0, zIndex:2 }}>Check</th>
            </tr>
          </thead>
          <tbody>
            {renderSection('Indirect Works', indirectTree, indirectStats, true)}
            {renderSection('Direct Works', directTree, directStats, false)}
            <tr style={{ background:'#1B3A6B', color:'#fff', fontWeight:700 }}>
              <td style={{ padding:'8px 10px',fontSize:12,fontWeight:700,color:'#fff',position:'sticky',left:0,background:'#1B3A6B' }}>GRAND TOTAL</td>
              <td style={{ ...tdS,color:'#fff',fontWeight:700 }}>{fmt(grandTotForecast)}</td>
              <td style={tdS} />
              <td style={{ ...tdS,color:'#fff',fontWeight:700 }}>{fmt(grandTotForecastWithVat)}</td>
              <td style={{ ...tdS,color:'#fff',fontWeight:700 }}>{fmt(grandTotPaid)}</td>
              <td style={{ ...tdS,color: grandTotRemaining < 0 ? '#FCA5A5' : '#fff',fontWeight:700 }}>{fmt(grandTotRemaining)}</td>
              {months.map(ym => {
                const t = [...directTree,...indirectTree].reduce((s,p)=>s+(directStats[p.code]?.monthTotals[ym] ?? indirectStats[p.code]?.monthTotals[ym] ?? 0),0)
                return <td key={ym} style={{ ...tdS,color:'#fff',fontWeight:700 }}>{t?fmt(t):'—'}</td>
              })}
              <td style={{ ...tdS,color:'#fff' }}/>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Floating horizontal scroll bar, fixed to the bottom of the
          viewport (not the table) — identical pattern to IPC.jsx. Only
          rendered when the table actually overflows horizontally. */}
      {scrollState.hasOverflow && (
        <div style={{
          position:'fixed', bottom:0, left:scrollNavLeftRight.left, right:scrollNavLeftRight.right, zIndex:50,
          display:'flex', alignItems:'center', gap:8,
          padding:'6px 12px', background:'rgba(255,255,255,0.97)',
          borderTop:'1px solid #E5E7EB', backdropFilter:'blur(2px)',
        }}>
          <button onClick={() => scrollBy(-1)} disabled={scrollState.atStart} aria-label="Scroll table left"
            style={{ width:24,height:24,borderRadius:6,border:'1px solid #E5E7EB',background:scrollState.atStart?'#F9FAFB':'#fff',color:scrollState.atStart?'#D1D5DB':'#6B7280',cursor:scrollState.atStart?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0 }}>‹</button>
          <input type="range" min={0} max={1000} step={1} value={Math.round(scrollState.ratio*1000)}
            onChange={e => scrollToRatio(Number(e.target.value)/1000)} aria-label="Scroll table horizontally"
            style={{ flex:1,height:4,accentColor:'#1B3A6B',cursor:'pointer' }}/>
          <button onClick={() => scrollBy(1)} disabled={scrollState.atEnd} aria-label="Scroll table right"
            style={{ width:24,height:24,borderRadius:6,border:'1px solid #E5E7EB',background:scrollState.atEnd?'#F9FAFB':'#fff',color:scrollState.atEnd?'#D1D5DB':'#6B7280',cursor:scrollState.atEnd?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0 }}>›</button>
        </div>
      )}

      {showSettings && renderSettingsModal()}
      {distModal && renderDistributeModal()}
    </div>
  )
}
